import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import type { ProductSelectionItem } from "../../Supplier/types/types"

import { DateSpecialValue } from "@/components/UiKit/AerosDatePicker/AerosDatePickerWithCustomActions"
import type { Location, Order, Product } from "@/graphql/codegen/graphql"
import type {
  ChangePricingUoMFormData,
  OrderLinePriceFormData,
  PurchaseOrderForm,
  ShipToLocationWithWarehouse,
} from "@/screens/Procurement/PurchaseOrder/types"
import { PACKAGING_REQUIRED_FIELDS } from "@/screens/Products/components/Product/Information/schemas/packaging"
import { AddressFormat } from "@/types/common"
import type { Option } from "@/types/form-fields"
import { isEmptyValue } from "@/utils/forms"
import { formatLocationAddress } from "@/utils/utils"

dayjs.extend(utc)

/**
 * Checks if the first date is the same as or after the second date.
 *
 * @param {string} date - The first date to compare.
 * @param {string} dueDate - The second date to compare.
 * @returns {boolean} - Returns true if the first date is the same as or after the second date, otherwise false.
 */
export const dateIsSameOrAfter = (date: string | null, startDate: string): boolean => {
  if (!date) {
    return false
  }
  const dateStartOfDay = dayjs.utc(date).startOf("day")
  const startDateStartOfDay = dayjs.utc(startDate).startOf("day")
  return dateStartOfDay.isSame(startDateStartOfDay) || dateStartOfDay.isAfter(startDateStartOfDay)
}

/**
 * Checks if the first date is before the second date.
 *
 * @param {string} date - The first date to compare.
 * @param {string} dueDate - The second date to compare.
 * @returns {boolean} - Returns true if the first date is before the second date, otherwise false.
 */
export const dateIsBefore = (date: string | null, dueDate: string): boolean => {
  if (!date) {
    return false
  }
  const dateStartOfDay = dayjs.utc(date).startOf("day")
  const dueDateStartOfDay = dayjs.utc(dueDate).startOf("day")
  return dateStartOfDay.isBefore(dueDateStartOfDay)
}

export const getPurchaseOrderForm = (purchaseOrder: Order): PurchaseOrderForm => {
  return {
    ...purchaseOrder,
    createdByUser: purchaseOrder.createdByUser!,
    sourceLocationAssociationId: purchaseOrder.sourceLocationAssociationId!,
    destinationLocationAssociationId: purchaseOrder.destinationLocationAssociationId!,
    requestedDeliveryDate: purchaseOrder.isRequestedDeliveryAsap
      ? DateSpecialValue.ASAP
      : purchaseOrder.requestedDeliveryDate!,

    confirmedShipmentDate: purchaseOrder.isConfirmedShipmentTbd
      ? DateSpecialValue.TBD
      : purchaseOrder.confirmedShipmentDate!,

    expectedDeliveryDate: purchaseOrder.expectedDeliveryDate ?? null,
    sentDate: purchaseOrder.sentDate!,
    createdOn: purchaseOrder.createdOn!,

    deliveryTerms: purchaseOrder.deliveryTerms ?? "",
    deliveryLocationId: purchaseOrder.deliveryLocationId ?? "",
    freightTerms: purchaseOrder.freightTerms ?? undefined,
    shipViaCompanyId: purchaseOrder.shipViaCompanyId ?? "",
    shipStatus: purchaseOrder.shipStatus ?? null,
    shippingLegs:
      purchaseOrder.shippingLegs?.map((leg) => ({
        ...leg,
        shippingLegId: leg.shippingLegId ?? "",
      })) ?? [],
    orderLines:
      purchaseOrder.orderLines?.map((orderLine) => ({
        isManualPriceSet: orderLine.isManualPriceSet ?? false,
        numberOfPallets: orderLine.numberOfPallets ?? 0,
        pricePerUom: orderLine.pricePerUom ?? 0,
        quantityInUom: orderLine.quantityInUom ?? 0,
        productId: orderLine.productId ?? 0,
        productVersionId: orderLine.productVersionId ?? 0,
        uomId: orderLine.uomId ?? "",
        priceUomId: orderLine.priceUomId ?? "",
        product: orderLine.product,
        orderLineId: orderLine.orderLineId,
      })) ?? [],
    note: purchaseOrder.note ?? "",
    currentComment: {},
    comments: [],
  }
}

export const getLocationWarehousesPrefix = (location: Location): string =>
  (location.warehouseAreas ?? [])
    .map(({ externalSystemId }) => externalSystemId)
    .sort()
    .join(" ")

export const isProductsInfoValid = (productOrProductList: Product | Product[] | null): boolean => {
  if (!productOrProductList) {
    return false
  }

  const productList = Array.isArray(productOrProductList) ? productOrProductList : [productOrProductList]

  return productList.every((product) => {
    return PACKAGING_REQUIRED_FIELDS.every((field) => {
      return !isEmptyValue(product[field as keyof Product])
    })
  })
}

export const hasValidShipFromLocations = (
  productOrProductList: Product | Product[] | ProductSelectionItem[] | null
): boolean => {
  if (!productOrProductList) {
    return false
  }

  const productList = Array.isArray(productOrProductList) ? productOrProductList : [productOrProductList]

  return productList.every((product) => {
    return !!product?.locationsAssociations?.[0]?.shipping
  })
}

export const getShipToLocationOptions = (locations: ShipToLocationWithWarehouse[]): Option<string>[] =>
  locations.map((location) => ({
    label: `${location.warehouseExternalSystemId} ${formatLocationAddress(location, AddressFormat.Shipping)}`,
    value: JSON.stringify({
      locationAssociationId: location.locationAssociationId,
      warehouseAreaId: location.warehouseAreaId,
    }),
  }))

export const mapOrderLinePriceFormData = ({
  orderLines,
  rowIndex,
  data,
}: {
  orderLines: OrderLinePriceFormData[]
  rowIndex: number
  data: ChangePricingUoMFormData
}): OrderLinePriceFormData[] =>
  orderLines?.map((line, index) => {
    if (index === rowIndex) {
      return {
        ...line,
        ...data,
        pricePerUom: data.pricePerUom,
      }
    }
    return {
      ...line,
      pricePerUom: line?.pricePerUom ?? 0,
    }
  }) || []
